'use client';

import { WAYS_OF_CONTACT_MAP } from '@/constants/ways-of-contact-map';
import JobService from '@/services/Job.service';
import styles from '@/styles/ways-of-contact.module.css';
import { Job } from '@/types/job';
import { WayOfContact } from '@/types/way-of-contact';
import { phoneMask } from '@/utils/phone-mask';
import { useState } from 'react';
import SimpleModal from '../modals/simple-modal';
import InterviewAddressModal from './interview-address-modal';
import { validWaysOfContact } from './valid-ways-of-contact';
import GtagService from '@/services/Gtag.service';
import Image from 'next/image';
import icoWhatsapp from '@/public/images/whatsapp_icon.svg';
import { formatJobTitle } from '@/utils/format-job-title';

interface Props {
  job: Job;
}

type WhatsappMessageInstructionsCacheEntry =
  | { status: 'pending'; promise: Promise<string | null> }
  | { status: 'resolved'; value: string | null };

const whatsappMessageInstructionsCache = new Map<number, WhatsappMessageInstructionsCacheEntry>();

const hasWhatsappContact = (job: Job) =>
  validWaysOfContact(job).includes('WHATSAPP') && !!job.contactPhones?.some(({ whatsapp }) => whatsapp);

const fetchWhatsappMessageInstructionsWithCache = (jobId: number) => {
  const cachedEntry = whatsappMessageInstructionsCache.get(jobId);

  if (cachedEntry?.status === 'resolved') {
    return Promise.resolve(cachedEntry.value);
  }

  if (cachedEntry?.status === 'pending') {
    return cachedEntry.promise;
  }

  const promise = JobService.fetchWhatsappMessageInstructions(jobId)
    .then((instructions) => {
      whatsappMessageInstructionsCache.set(jobId, { status: 'resolved', value: instructions });
      return instructions;
    })
    .catch((error) => {
      console.error(error);
      whatsappMessageInstructionsCache.delete(jobId);
      return null;
    });

  whatsappMessageInstructionsCache.set(jobId, { status: 'pending', promise });
  return promise;
};

export const prefetchWhatsappMessageInstructions = (job: Job) => {
  if (!hasWhatsappContact(job)) {
    return Promise.resolve(null);
  }

  return fetchWhatsappMessageInstructionsWithCache(job.id);
};

export default function WaysOfContactList({ job }: Props) {
  const { waysOfContact, id } = job;

  const whatsAppContact = job?.contactPhones?.find(({ whatsapp }) => whatsapp);

  const hasContactByAddress = waysOfContact.includes(`ATTEND`) && job.interviewAddress && job.schedule.length > 0;

  const [isPhoneActive, setPhoneActive] = useState(false);
  const toggleClassPhone = () => {
    if (!isPhoneActive) {
      JobService.applyWithPhone(id);

      GtagService.eventCadastroVaga({ type: job.slug, status: `candidatura-logado`, contact: `Telefone` });

    }
    setPhoneActive(!isPhoneActive);
  };

  const [isWhatsAppActive, setWhatsAppActive] = useState(false);
  const [whatsappMessageInstructions, setWhatsappMessageInstructions] = useState<string | null>(null);
  const [isLoadingWhatsappMessageInstructions, setLoadingWhatsappMessageInstructions] = useState(false);
  const toggleModalWhatsApp = () => {
    if (isLoadingWhatsappMessageInstructions) {
      return;
    }

    if (!isWhatsAppActive) {
      JobService.applyWithWhatsApp(id);

      const cachedEntry = whatsappMessageInstructionsCache.get(id);
      if (cachedEntry?.status === 'resolved') {
        setWhatsappMessageInstructions(cachedEntry.value);
        setWhatsAppActive(true);
        GtagService.eventCadastroVaga({ type: job.slug, status: `candidatura-logado`, contact: `WhatsApp` });
        return;
      }

      setLoadingWhatsappMessageInstructions(true);
      fetchWhatsappMessageInstructionsWithCache(id)
        .then((instructions) => setWhatsappMessageInstructions(instructions))
        .finally(() => {
          setLoadingWhatsappMessageInstructions(false);
          setWhatsAppActive(true);
        });

      GtagService.eventCadastroVaga({ type: job.slug, status: `candidatura-logado`, contact: `WhatsApp` });
      return;
    }

    setWhatsAppActive(!isWhatsAppActive);
  };

  const [isAddressActive, setAddressActive] = useState(false);
  const toggleClassAddress = () => {
    if (!isAddressActive) {
      JobService.applyWithAdress(id);

      GtagService.eventCadastroVaga({ type: job.slug, status: `candidatura-logado`, contact: `Local` });
    }
    setAddressActive(!isAddressActive);
  };

  const [isSiteActive, setSiteActive] = useState(false);
  const toggleClassSite = () => {
    if (!isSiteActive) {
      JobService.applyWithSite(id);
    }
    setSiteActive(!isSiteActive);
  };


  const [isEmailActive, setEmailActive] = useState(false);
  const toggleClassEmail = () => {
    if (!isEmailActive) {
      JobService.applyWithSite(id);
    }
    setEmailActive(!isEmailActive);
  };

  const toggleModalMap = {
    ATTEND: toggleClassAddress,
    PHONE: toggleClassPhone,
    SEND_EMAIL: toggleClassEmail,
    SEND_RESUME: () => console.log(`enviar currículo`),
    WEBSITE: toggleClassSite,
    WHATSAPP: toggleModalWhatsApp,
  };

  const toggleModal = (wayOfContact: WayOfContact) => {
    toggleModalMap[wayOfContact]();
  };

  return (
    <>
      <ul className={styles.actions}>
        {validWaysOfContact(job).map((wayOfContact) => (
          <li key={`modal-item-ways-of-contact-${wayOfContact}`}>
            <button
              type="button"
              onClick={() => toggleModal(wayOfContact)}
              className="btn btn-small btn-outline"
              disabled={wayOfContact === 'WHATSAPP' && isLoadingWhatsappMessageInstructions}
            >
              {wayOfContact === 'WHATSAPP' && isLoadingWhatsappMessageInstructions ? (
                <span className={styles.action_loading}>
                  <span className={styles.action_spinner} aria-hidden="true" />
                  Carregando
                </span>
              ) : (
                WAYS_OF_CONTACT_MAP[wayOfContact]
              )}
            </button>
          </li>
        ))}
      </ul>

      <SimpleModal open={isWhatsAppActive} onClose={toggleModalWhatsApp} className={styles.contact_container}>
        <h3>WhatsApp</h3>
        {whatsappMessageInstructions && (
          <div className={styles.whatsapp_message_instructions}>
            <strong>Orientações para enviar mensagem</strong>
            <p>{whatsappMessageInstructions}</p>
          </div>
        )}
        <ul>
          {whatsAppContact && (
            <li key={`li-contact-phone-${whatsAppContact.number}`}>
              <div className={styles.contact_content}>
                <a
                  target="_blank"
                  href={`https://api.whatsapp.com/send?phone=55${whatsAppContact.number}`}
                  rel="noreferrer"
                  onClick={() => {
                    GtagService.eventCadastroVaga({
                      type: job.slug,
                      status: `candidatura-logado-assinante-mensagem`,
                      contact: `WhatsApp`,
                    });
                  }}
                >
                  {phoneMask(whatsAppContact.number)}
                </a>
                <a
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  target="_blank"
                  href={`https://api.whatsapp.com/send?phone=55${whatsAppContact.number}&text=${encodeURIComponent(`Olá! Vi a vaga *${formatJobTitle(job.title)}* no Amarelinho e gostaria de mais informações.`)}`}
                  rel="noreferrer"
                  className="btn btn-small btn-secondary"
                  onClick={() => {
                    GtagService.eventCadastroVaga({
                      type: job.slug,
                      status: `candidatura-logado-assinante-mensagem`,
                      contact: `WhatsApp`,
                    });
                  }}
                >
                  Enviar WhatsApp <Image width={25} height={25} src={icoWhatsapp} alt="Whatsapp" />
                </a>
              </div>
            </li>
          )}
        </ul>
      </SimpleModal>

      <SimpleModal open={isPhoneActive} onClose={toggleClassPhone} className={styles.contact_container}>
        <h3>Telefone</h3>
        <span>
          <ul>
            {job?.contactPhones
              ?.filter(({ phoneCall }) => phoneCall)
              ?.map(({ number }) => (
                <li key={`contact-phone-${number}`}>
                  <div  className={styles.contact_content}>
                    <a
                      href={`tel:55${number}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => {
                        GtagService.eventCadastroVaga({
                          type: job.slug,
                          status: `candidatura-logado-assinante-ligar`,
                          contact: `Telefone`
                        });
                      }}
                    >
                      {phoneMask(number)}
                    </a>
                  </div>
                </li>
              ))}
          </ul>
        </span>
      </SimpleModal>

      <SimpleModal open={isSiteActive} onClose={toggleClassSite} className={styles.contact_container}>
        <h3>Site</h3>
        <div className={styles.contact_content}>
          <a href={job.site} target="_blank" rel="noreferrer">
            {job.site}
          </a>
        </div>
      </SimpleModal>

      <SimpleModal open={isEmailActive} onClose={toggleClassEmail} className={styles.contact_container}>
        <h3>E-mail</h3>
        <a href={'mailto:'+job.email} target="_blank" rel="noreferrer">
          {job.email}
        </a>
      </SimpleModal>

      {hasContactByAddress && <InterviewAddressModal job={job} open={isAddressActive} onClose={toggleClassAddress} />}
    </>
  );
}
